import React, { createContext, useContext, useMemo, ReactNode } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { getGoogleMapsApiKey, GOOGLE_MAPS_LIBRARIES } from "@/lib/googleMaps";

interface GoogleMapsContextType {
    isLoaded: boolean;
    loadError: Error | undefined;
}

const GoogleMapsContext = createContext<GoogleMapsContextType | undefined>(undefined);

// Define libraries outside component to prevent re-renders
const libraries = GOOGLE_MAPS_LIBRARIES;

export function GoogleMapsProvider({ children }: { children: ReactNode }) {
    const apiKey = getGoogleMapsApiKey();

    // Use the official loader from @react-google-maps/api
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: apiKey,
        libraries: libraries,
        id: 'google-map-script',
        language: 'nl', // Default to Dutch
        version: "weekly"
    });

    const value = useMemo(() => ({
        isLoaded,
        loadError,
    }), [isLoaded, loadError]);

    return (
        <GoogleMapsContext.Provider value={value}>
            {children}
        </GoogleMapsContext.Provider>
    );
}

export function useGoogleMaps() {
    const context = useContext(GoogleMapsContext);
    if (context === undefined) {
        throw new Error("useGoogleMaps must be used within a GoogleMapsProvider");
    }
    return context;
}
